import { Box, Button, CircularProgress, Grid } from "@mui/material";
import React, { useRef } from "react";
import Badge from "../../../components/Badge/Badge";
import CardBody from "../../../components/Card/CardBody";
import Card from "../../../components/Card/Card";
import GridContainer from "../../../components/Grid/GridContainer";
import GridItem from "../../../components/Grid/GridItem";
import { Pagination } from "@mui/lab";
import axios from "axios";
import { format, formatRelative } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { FileCopy } from "@mui/icons-material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";

function ChangelogV2(props) {
  const [pagination, setPagination] = React.useState({ page: 1, rowsPerPage: 10, changelog: null, count: 0 });

  const { page, rowsPerPage, changelog, count: pageCount } = pagination;
  const axiosRequest = useRef(null);

  React.useEffect(() => {
    const url = props.endPoint + `/changelog?per_page=${rowsPerPage}&page=${page}`;
    if (axiosRequest.current) {
      axiosRequest.current.cancel();
    }

    axiosRequest.current = axios.CancelToken.source();

    axios.get(url, { cancelToken: axiosRequest.current.token }).then((response) => {
      setPagination({
        page: page !== response.data.data.current_page ? response.data.data.current_page : page,
        rowsPerPage: rowsPerPage,
        changelog: response.data.data.data,
        count: Math.ceil(response.data.data.total / rowsPerPage),
      });
    });
  }, [page, rowsPerPage]);

  if (!changelog) {
    return <CircularProgress color={"primary"} />;
  }

  return (
    <>
      <Card sx={{ width: "100%" }}>
        <CardBody>
          <Pagination
            color={"primary"}
            count={pageCount}
            hideNextButton={pageCount < 5}
            hidePrevButton={pageCount < 5}
            onChange={(e, page) => {
              setPagination({ ...pagination, page: page });
            }}
            page={page}
            showFirstButton={pageCount > 4}
            showLastButton={pageCount > 4}
          />
        </CardBody>
      </Card>
      {changelog.map((item) => {
        let badgeColor = "success";

        switch (item.event) {
          case "restored":
            badgeColor = "warning";
            break;
          case "deleted":
            badgeColor = "danger";
            break;
        }

        const created_at_relative = formatRelative(new Date(item.created_at), new Date());
        const created_at_absolute = format(new Date(item.created_at), "eee do 'of' MMM Y 'at' HH:mm:ss");

        return item.old_values !== null && item.new_values !== null ? (
          <Card key={"itemId." + item.id} sx={{ width: "100%" }}>
            <CardBody>
              <GridContainer>
                <GridItem>
                  <Box sx={{ mb: 2 }}>
                    <Badge color={badgeColor} sx={{ mr: 1 }}>
                      {item.event}
                    </Badge>
                    <Tooltip title={created_at_absolute}>
                      <Badge color={"info"} sx={{ mr: 1 }}>
                        {created_at_relative}
                      </Badge>
                    </Tooltip>
                    <Badge color={"gray"} sx={{ mr: 1 }}>
                      {item.user ? "By " + item.user.email : "System"}{" "}
                      {item.impersonating_user ? "Impersonated by " + item.impersonating_user.email : ""}
                    </Badge>
                    {item.tags !== null
                      ? item.tags.split(",").map((tag, key) => {
                          return (
                            <Badge color={"warning"} key={"tag_" + key} sx={{ mr: 1, fontSize: "14px" }}>
                              <b>{tag}</b>
                            </Badge>
                          );
                        })
                      : null}
                  </Box>
                </GridItem>
              </GridContainer>
              {item.event === "updated" || item.event === "created"
                ? Object.keys(item.new_values).map((key) => {
                    let old_item = item.old_values[key] ?? "";
                    let new_item = item.new_values[key];

                    try {
                      old_item = JSON.parse(old_item);
                    } catch (e) {}
                    try {
                      new_item = JSON.parse(new_item);
                    } catch (e) {}

                    if (typeof old_item !== "string") {
                      old_item = JSON.stringify(old_item, null, 4);
                    }
                    if (typeof new_item !== "string") {
                      new_item = JSON.stringify(new_item, null, 4);
                    }

                    return (
                      <Grid container key={"changeLogViewOld" + key}>
                        <Grid item style={{ width: "100%" }}>
                          <Box
                            style={{
                              position: "sticky",
                              top: "0",
                              background: "#fafafa",
                              height: "25px",
                              color: "#303030",
                              fontWeight: "bold",
                              padding: "12px",
                              border: "1px solid #dbdbdb",
                              borderRadius: "4px 4px 0 0",
                              boxSizing: "initial",
                              fontSize: "18px",
                            }}
                          >
                            {key}
                            <CopyToClipboard onCopy={() => toast.success("Copied new value")} text={new_item}>
                              <Button
                                size={"small"}
                                startIcon={<FileCopy />}
                                style={{ float: "right", color: "#7abd8a" }}
                              >
                                Copy new
                              </Button>
                            </CopyToClipboard>
                            <CopyToClipboard onCopy={() => toast.success("Copied old value")} text={old_item}>
                              <Button
                                disabled={old_item === ""}
                                size={"small"}
                                startIcon={<FileCopy />}
                                style={{
                                  marginLeft: "8px",
                                  marginRight: "8px",
                                  float: "right",
                                  ...(old_item === "" ? { color: "gray" } : { color: "#f9786a" }),
                                }}
                              >
                                Copy old
                              </Button>
                            </CopyToClipboard>
                          </Box>
                          <Box
                            sx={{
                              mb: 2,
                              borderLeft: "1px solid #dbdbdb",
                              borderRight: "1px solid #dbdbdb",
                              borderBottom: "1px solid #dbdbdb",
                              borderRadius: "0 0 4px 4px",
                            }}
                          >
                            <ReactDiffViewer
                              compareMethod={DiffMethod.WORDS}
                              extraLinesSurroundingDiff={2}
                              hideLineNumbers={true}
                              newValue={new_item == null ? {} : new_item}
                              oldValue={old_item == null ? {} : old_item}
                              splitView={true}
                              styles={{
                                marker: {
                                  display: "none",
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    );
                  })
                : null}
            </CardBody>
          </Card>
        ) : null;
      })}
      <Card>
        <CardBody>
          <Pagination
            color={"primary"}
            count={pageCount}
            hideNextButton={pageCount < 5}
            hidePrevButton={pageCount < 5}
            onChange={(e, page) => {
              setPagination({ ...pagination, page: page });
            }}
            page={page}
            showFirstButton={pageCount > 4}
            showLastButton={pageCount > 4}
          />
        </CardBody>
      </Card>
    </>
  );
}

export default ChangelogV2;
